"use client";

import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { useRouter } from "next/navigation";
import { RequestError, requestJson } from "@/lib/clientRequest";
import type { PaletteWorker } from "@/lib/commandPalette";

/** The two quick actions that call the API, and the toast that reports on them. */
export function usePaletteActions({
  workers,
  setWorkers,
  setOpen,
}: {
  workers: PaletteWorker[];
  setWorkers: Dispatch<SetStateAction<PaletteWorker[]>>;
  setOpen: (open: boolean) => void;
}) {
  const router = useRouter();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [statusActionPending, setStatusActionPending] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3500);
  }, []);

  function dismissToast() {
    setToastMessage(null);
  }

  // Action: Run Worker
  const handleTriggerWorker = useCallback(
    async (worker: PaletteWorker) => {
      setStatusActionPending(true);
      try {
        await requestJson(`/api/workers/${worker.id}/run`, {
          method: "POST",
          body: {},
          fallback: `Failed to run "${worker.name}"`,
        });
        showToast(`Successfully triggered "${worker.name}"`);
        setTimeout(() => {
          setOpen(false);
          router.push(`/workers/${worker.id}?tab=runs`);
        }, 800);
      } catch (cause) {
        showToast(
          cause instanceof RequestError
            ? cause.message
            : `Failed to trigger "${worker.name}"`
        );
      } finally {
        setStatusActionPending(false);
      }
    },
    [router, setOpen, showToast]
  );

  // Action: Toggle All Workers (Pause / Resume)
  const handleToggleAllWorkers = useCallback(async () => {
    if (workers.length === 0) {
      showToast("No workers found to toggle");
      return;
    }
    const anyPaused = workers.some((w) => !w.enabled);
    const nextEnabled = anyPaused; // If any is paused, resume all; otherwise pause all
    setStatusActionPending(true);
    try {
      await requestJson("/api/workers", {
        method: "PATCH",
        body: { enabled: nextEnabled },
        fallback: "Failed to toggle workers",
      });
      showToast(
        nextEnabled
          ? `Resumed all ${workers.length} worker(s)`
          : `Paused all ${workers.length} worker(s)`
      );
      // Refresh local worker list and emit app event
      window.dispatchEvent(new CustomEvent("caw:activity-refresh"));
      setWorkers(current => current.map(worker => ({ ...worker, enabled: nextEnabled })));
    } catch (cause) {
      showToast(cause instanceof RequestError ? cause.message : "Error updating workers");
    } finally {
      setStatusActionPending(false);
    }
  }, [workers, showToast, setWorkers]);

  return {
    toastMessage,
    dismissToast,
    statusActionPending,
    handleTriggerWorker,
    handleToggleAllWorkers,
  };
}
